import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Subject, of, from } from 'rxjs';
import { switchMap, map, tap, catchError, takeUntil } from 'rxjs/operators';
import { ProfileService } from '../profile/profile.service';
import { LogRocketService } from '../../shared/services/log-rocket.service';
import { LogRocketUser } from '../../shared/models/log-rocket.model';
import { OnboardingService } from '../../chatstyle/onboarding/onboarding.service';
import { OrganizationService } from '../../chatstyle/organization/organization.service';
import { OrgNotificationService } from '../../shared/services/org-notification.service';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  showProvisioning = false;
  private static readonly PROVISIONING_FLOOR_MS = 3000;

  private navigateAfterFloor(url: string) {
    this.showProvisioning = true;
    setTimeout(() => this.router.navigateByUrl(url), AuthCallbackComponent.PROVISIONING_FLOOR_MS);
  }

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private profileService: ProfileService,
    private router: Router,
    private logRocketService: LogRocketService,
    private onboardingService: OnboardingService,
    private orgService: OrganizationService,
    private orgNotificationService: OrgNotificationService
  ) { }

  ngOnInit() {
    this.oidcSecurityService.checkAuth().pipe(
      switchMap(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          // Do NOT navigate here. Throwing lets the catchError below own the single routing
          // decision; navigating first meant two competing navigations and the error handler's
          // won — sending an unauthenticated caller to the wizard rather than home (CHAT-877).
          throw new Error('Authentication failed');
        }
        return this.profileService.load();
      }),
      // Wait for the LogRocket session to be live before identifying, otherwise identify() lands
      // before there is a session to attach it to and the recording shows up anonymous (CHAT-299).
      // This resolves immediately when LogRocket is disabled (every non-prod environment) and via
      // an internal timeout when its ingest is blocked, so it never delays the routing below.
      switchMap(profile => from(this.logRocketService.initialize()).pipe(map(() => profile))),
      tap(profile => {
        // Identify user in LogRocket
        if (profile && profile.email) {
          const logRocketUser: LogRocketUser = {
            id: profile.email,
            name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.email,
            email: profile.email,
            companyName: profile.companyName,
            status: profile.status,
            privileges: profile.privileges
          };

          this.logRocketService.identifyUser(logRocketUser);
        }

        // Connect to org SignalR group for real-time notifications.
        // Fire-and-forget — errors are logged but do not block the routing decision below.
        this.orgService.getMyOrganization().subscribe({
          next: (org) => {
            if (org && org.id) {
              this.orgNotificationService.connect(org.id);
            }
          },
          error: (err) => console.warn('Could not load org for notifications:', err)
        });
      }),
      // Active users are past onboarding by definition — go straight to the launchpad without
      // hitting /onboarding/state (which would read _Tenants.ChatProvisioningStates, staging
      // data we want to be free to prune). Only mid-wizard cold-start users need the server's
      // shouldRedirectToLaunchpad signal.
      switchMap(() => {
        if (this.profileService.isActive()) {
          this.router.navigateByUrl('/');
          return of(null);
        }
        return this.onboardingService.getOnboardingState().pipe(
          tap(state => {
            // Only enter the wizard on an EXPLICIT negative. The old ternary treated a null or
            // empty response as "go to the wizard", so an ambiguous answer was enough to drop an
            // established tenant into onboarding — same defect as the auth guard had (CHAT-877).
            const sendToWizard = state?.shouldRedirectToLaunchpad === false;
            this.router.navigateByUrl(sendToWizard ? '/setup/setupwizard/step1' : '/');
          })
        );
      }),
      catchError((error: any) => {
        // Fail toward the launchpad, never into the wizard. This used to hard-code
        // '/setup/setupwizard/step1', so ANY failure in this pipeline — including one that happened
        // before the profile had even loaded — dropped a fully onboarded user into onboarding, which
        // is the most disruptive possible landing (CHAT-877). '/' is guarded, so the guard makes the
        // real decision once state is available; an unauthenticated caller is redirected to login
        // from there rather than being shown a setup wizard they cannot complete.
        console.error('auth-callback: routing decision failed, falling back to the launchpad', error);
        this.router.navigateByUrl('/');
        throw error;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      error: (err) => {
        console.error('Observable error in auth-callback:', err);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
